import React, { useState, useRef, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Select,
  MenuItem,
  TextField,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableContainer,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Tooltip,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import { AddBox as AddIcon } from "@material-ui/icons";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import { makeStyles } from "@material-ui/core/styles";

/* ------------------------- Importaciones internas ------------------------- */
import { api } from "../../../../services/api";
import { cancelButton, saveButton } from "../../../../core/styles/ui.constants";
import { ADMINISTRACION, ADMINISTRADOR } from "../../../../core/utils/roles.constants";
import { useLoading } from "../../../../contexts/LoadingContext";
import AgregarItemsModal from "./itemsModal";
import BotonGenerico from "../../../../components/Button/Button";
import DialogFooter from "../../../../components/DialogFooter/DialogActions";
import ABMProveedorModal from "./createProveedores";
import { useAuth } from "../../../../contexts/AuthContext";

/* --------------------------------- Estilos -------------------------------- */
const useStyles = makeStyles((theme) => ({
  dialogContent: {
    padding: theme.spacing(4),
    minWidth: "800px", // Aumentar el ancho del modal
  },
  closeButton: {
    position: "absolute",
    top: theme.spacing(1),
    right: theme.spacing(1),
  },
  titleText: {
    textAlign: "center",
    marginBottom: theme.spacing(3),
  },
  formRow: {
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2),
    gap: theme.spacing(2), // Agregar espacio entre los elementos
  },
  halfField: {
    width: "49%",
  },
  sectorField: {
    width: "32%", // Ajustar el ancho para que los campos tengan espacio
  },
  conceptoField: {
    width: "32%",
  },
  dateField: {
    width: "32%",
  },
  observationsField: {
    width: "58%",
  },
  radioGroup: {
    display: "flex",
    alignItems: "center",
  },
  buttonArea: {
    display: "flex",
    justifyContent: "flex-end",
    marginBottom: theme.spacing(2),
  },
  actionButton: {
    marginLeft: theme.spacing(1),
    saveButton
  },
  tableContainer: {
    marginTop: theme.spacing(2),
  },
  itemText: {
    marginBottom: theme.spacing(1),
  },
  footerText: {
    display: "flex",
    alignItems: "center",
    marginTop: theme.spacing(3),
  },
  footerButtons: {
    display: "flex",
    justifyContent: "flex-end",
    marginTop: theme.spacing(2),
  },
  footerButton: {
    marginLeft: theme.spacing(2),
  },
  areaBlanca: {
    backgroundColor: "#f5f5f5",
    padding: theme.spacing(2),
  },
  // actionButton: saveButton,
  cancelButton: cancelButton,
}));

const CrearOrdenDeServiciosModal = ({
  open,
  onClose,
  listaPersonal,
  listaSector,
  onSuccess,
  ordenServicio,
  onAddProveedor,
}) => {
  const classes = useStyles();
  const { show, hide } = useLoading();

  const [openItemsModal, setOpenItemsModal] = useState(false);
  const fileInputRef = useRef(null);
  const [currency, setCurrency] = useState("ARS");
  const [items, setItems] = useState([]);
  const [importeTotal, setImporteTotal] = useState(0);
  const [resetFormulario, setResetFormulario] = useState(false);
  const [tipoDeCambio, setTipoDeCambio] = useState(1);
  const [sectores, setSectores] = useState([]);
  const [originalItems, setOriginalItems] = useState([]);
  const [itemModalData, setItemModalData] = useState(null);
  const [itemModalIndex, setItemModalIndex] = useState(null);

  const { getUser } = useAuth();
  const user = getUser();
  const userSectorIds = Array.isArray(user?.sectores) && user.sectores.length > 0
    ? user.sectores.map((sector) => String(sector))
    : user?.IdSector ? [String(user.IdSector)] : [];
  const canSelectAnySector = [ADMINISTRADOR, ADMINISTRACION].includes(Number(user?.es_admin));

  const initialState = {
    fecha: new Date().toISOString().split("T")[0], // Fecha de creación
    fechaEntrega: new Date().toISOString().split("T")[0], // Fecha de entrega
    plazoEntrega: new Date().toISOString().split("T")[0], // Plazo de entrega
    sector: (user?.sectores && user.sectores[0]) || user.IdSector || "",
    solicitante: "",
    codigoPresupuesto: "",
    diasHabiles: 15,
    moneda: "ARS", // Puede tener un valor por defecto
    importeTotal: "",
  };
  const [state, setState] = useState(initialState);
  const [errors, setErrors] = useState({});

  // Agregar proveedor
  const [openProveedor, setOpenProveedor] = useState(false);

  const handleCloseProveedor = () => setOpenProveedor(false);

  const handleNuevoClick = () => {
    setResetFormulario(true); // Señal de reinicio
    setItemModalData(null);
    setItemModalIndex(null);
    setOpenItemsModal(true);
  };

  const handleCloseItemsModal = () => {
    setOpenItemsModal(false);
    setResetFormulario(false); // Reinicio completado
    setItemModalData(null);
    setItemModalIndex(null);
  };

  const recalculateTotals = (itemsList, selectedCurrency = currency, selectedTipoDeCambio = tipoDeCambio) => {
    const subtotal = itemsList.reduce(
      (acc, item) => acc + parseFloat(item.precioTotal || 0),
      0
    );
    const total = selectedCurrency === "USD" ? subtotal * selectedTipoDeCambio : subtotal;

    setImporteTotal(total);
    setState((prev) => ({
      ...prev,
      importeTotal: total.toFixed(2),
    }));

    return total;
  };

  const handleCurrencyChange = (event) => {
    const nuevaMoneda = event.target.value;
    setCurrency(nuevaMoneda);
    setErrors((prevErrors) => {
      if (!prevErrors.moneda) return prevErrors;
      const nextErrors = { ...prevErrors };
      delete nextErrors.moneda;
      return nextErrors;
    });

    const subtotal = items.reduce(
      (acc, item) => acc + parseFloat(item.precioTotal || 0),
      0
    );
    const total = nuevaMoneda === "USD" ? subtotal * tipoDeCambio : subtotal;
    setImporteTotal(total);

    setState((prev) => ({
      ...prev,
      moneda: nuevaMoneda,
      importeTotal: total.toFixed(2),
    }));
  };

  const handleGuardarItem = (nuevoItem) => {
    setErrors((prevErrors) => {
      if (!prevErrors.items) return prevErrors;
      const nextErrors = { ...prevErrors };
      delete nextErrors.items;
      return nextErrors;
    });

    setItems((prevItems) => {
      const nuevosItems =
        itemModalIndex === null
          ? [...prevItems, nuevoItem]
          : prevItems.map((item, index) => (index === itemModalIndex ? { ...item, ...nuevoItem } : item));

      recalculateTotals(nuevosItems);
      return nuevosItems;
    });
  };

  const handleEditarItem = (item, index) => {
    setResetFormulario(false);
    setItemModalData(item);
    setItemModalIndex(index);
    setOpenItemsModal(true);
  };

  const handleEliminarItem = (index) => {
    const nuevosItems = items.filter((_, i) => i !== index);
    setItems(nuevosItems);
    recalculateTotals(nuevosItems);
  };

  const formatearImporte = (valor) => {
    const monto = parseFloat(valor || 0).toFixed(2);
    return `$ ${monto}`; // Siempre en ARS
  };

  const handleChange = (field, value) => {
    switch (field) {
      case 'diasHabiles':
        if (value !== "" && (!/^\d+$/.test(value) || Number(value) < 1 || Number(value) > 99)) {
          return;
        }
        break;
      default:
        break;
    }

    setErrors((prevErrors) => {
      if (!prevErrors[field]) return prevErrors;
      const nextErrors = { ...prevErrors };
      delete nextErrors[field];
      return nextErrors;
    });

    setState((prevState) => ({
      ...prevState,
      [field]: value,
    }));
  };

  const validate = () => {
    const newErrors = {};

    if (!state.fecha) newErrors.fecha = "La fecha es obligatoria.";
    if (!state.fechaEntrega)
      newErrors.fechaEntrega = "La fecha de entrega es obligatoria.";
    if (!state.plazoEntrega)
      newErrors.plazoEntrega = "El plazo de entrega es obligatorio.";
    if (!state.sector) newErrors.sector = "El sector es obligatorio.";
    if (
      state.sector &&
      !canSelectAnySector &&
      !userSectorIds.includes(String(state.sector))
    ) {
      newErrors.sector = "Solo puede seleccionar sectores asignados a su usuario.";
    }
    if (!state.solicitante)
      newErrors.solicitante = "El cliente es obligatorio.";
    if (!state.moneda) newErrors.moneda = "La moneda es obligatoria.";

    if (items.length === 0) {
      newErrors.items = "Debe agregar al menos un item a la orden de servicio.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    show("Guardando orden de servicio...");
    const payload = {
      FechaCreacion: state.fecha,
      fechaEntrega: state.fechaEntrega,
      plazoEntrega: state.plazoEntrega,
      idSector: state.sector,
      IdUsuario: state.solicitante,
      codigoPresupuesto: state.codigoPresupuesto,
      diasHabiles: state.diasHabiles !== "" ? Number(state.diasHabiles) : null,
      moneda: state.moneda,
      importeTotal: state.importeTotal,
      tipoDeCambio: tipoDeCambio,
      estadoOS: 1,
      oculto: 0,
    };

    // console.log("Payload", payload);

    try {
      if (ordenServicio?.idOS) {
        // 🔄 Editar orden existente
        await api.ordenesServicios.update(ordenServicio.idOS, payload);
        const itemsExistentes = originalItems.filter((item) => item.idItemsOS);
        const itemsActualesConId = items.filter((item) => item.idItemsOS);
        const itemsEliminados = itemsExistentes.filter(
          (itemOriginal) =>
            !itemsActualesConId.some((itemActual) => itemActual.idItemsOS === itemOriginal.idItemsOS)
        );

        await Promise.all(
          items.map((item) => {
            const itemPayload = {
              idOrdenServicio: ordenServicio.idOS,
              itemName: item.itemName,
              cantidad: item.cantidad,
              precioUnitario: item.precioUnitario,
              descripcion: item.descripcion,
            };

            if (item.idItemsOS) {
              return api.itemsOS.update(item.idItemsOS, itemPayload);
            }

            return api.itemsOS.create(itemPayload);
          })
        );

        await Promise.all(
          itemsEliminados.map((item) => api.itemsOS.delete(item.idItemsOS))
        );
      } else {
        // 🆕 Crear nueva orden
        const ordenResult = await api.ordenesServicios.create(payload);
        const idOrdenServicio = ordenResult.idOS;

        for await (const item of items) {
          await api.itemsOS.create({
            idOrdenServicio: idOrdenServicio,
            itemName: item.itemName,
            cantidad: item.cantidad,
            precioUnitario: item.precioUnitario,
            descripcion: item.descripcion,
          });
        }
      }

      onSuccess(); // notificar éxito
      onClose(); // cerrar modal
      valoresDefault();
    } catch (err) {
      console.error("Error al guardar orden de servicio:", err.message);
      alert("Ocurrió un error al guardar la orden. Intenta nuevamente.");
    } finally {
      hide();
    }
  };

  const valoresDefault = () => {
    setState(initialState);
    setErrors({});
    setItems([]);
    setOriginalItems([]);
    setImporteTotal(0);
    setCurrency("ARS");
    setTipoDeCambio(1);
    setItemModalData(null);
    setItemModalIndex(null);
  };

  useEffect(() => {
    if (open) {
      const hydrateModal = async () => {
        if (ordenServicio) {
          setCurrency(ordenServicio.moneda || "ARS");
          setState({
            fecha: ordenServicio.FechaCreacion,
            fechaEntrega: ordenServicio.fechaEntrega,
            plazoEntrega: ordenServicio.plazoEntrega,
            sector: ordenServicio.idSector,
            moneda: ordenServicio.moneda,
            importeTotal: ordenServicio.importeTotal,
            codigoPresupuesto: ordenServicio.codigoPresupuesto || "",
            diasHabiles: ordenServicio.diasHabiles ?? 15,
            solicitante: ordenServicio.IdUsuario,
          });

          const itemsOSResponse = await api.itemsOS.getAll();
          const itemsDeLaOrden = itemsOSResponse
            .filter((item) => item.idOrdenServicio === ordenServicio.idOS)
            .map((item) => ({
              ...item,
              precioTotal: (
                (parseFloat(item.cantidad) || 0) * (parseFloat(item.precioUnitario) || 0)
              ).toFixed(2),
            }));

          setItems(itemsDeLaOrden);
          setOriginalItems(itemsDeLaOrden);

          const subtotal = itemsDeLaOrden.reduce(
            (acc, item) => acc + parseFloat(item.precioTotal || 0),
            0
          );
          setImporteTotal(subtotal);
        } else {
          setCurrency(initialState.moneda);
          setState(initialState);
          setItems([]);
          setOriginalItems([]);
          setImporteTotal(0);
        }

        const resultados = listaSector.map((sector) => ({
          id: sector.IdSector,
          nombre: sector.Nombre,
        }));

        setSectores(resultados);
      };

      hydrateModal();
    } else {
      valoresDefault();
    }
  }, [open]);

  useEffect(() => {
    if (listaPersonal.length > 0 && !state.solicitante) {
      setState((prevState) => ({
        ...prevState,
        solicitante: listaPersonal[0].idProveedor,
      }));
    }
  }, [listaPersonal]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md">
      <DialogTitle disableTypography>
        <Typography variant="h6" className={classes.titleText}>
          Crear Orden de Servicios
        </Typography>
        <IconButton
          aria-label="close"
          className={classes.closeButton}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent className={classes.dialogContent}>
        {/* Fila de Sector y Cliente */}
        <div className={classes.formRow}>
          <div className={classes.halfField}>
            <Typography variant="subtitle1">Sector</Typography>
            <Select
              variant="outlined"
              fullWidth
              defaultValue=""
              value={state.sector || ""}
              onChange={(e) => handleChange("sector", e.target.value)}
              error={!!errors.sector || undefined}
              disabled={Boolean(ordenServicio)}
            >
              {sectores.map((sector) => (
                <MenuItem
                  key={sector.id}
                  value={sector.id}
                  disabled={!canSelectAnySector && !userSectorIds.includes(String(sector.id))}
                >
                  {sector.nombre}
                </MenuItem>
              ))}
            </Select>
            {errors.sector && (
              <Typography color="error" variant="body2">
                {errors.sector}
              </Typography>
            )}
          </div>
          <div className={classes.halfField}>
            <ABMProveedorModal
              open={openProveedor}
              onClose={handleCloseProveedor}
              onSuccess={onAddProveedor}
              tipoCliente="cliente"
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <Typography variant="subtitle1">Cliente</Typography>
              <Tooltip
                title={
                  <span style={{ fontSize: "0.8rem" }}>Agregar cliente</span>
                }
                arrow
              >
                <IconButton
                  color="primary"
                  onClick={() => setOpenProveedor(true)}
                  size="small"
                >
                  <AddIcon />
                </IconButton>
              </Tooltip>
            </div>
            <Select
              variant="outlined"
              fullWidth
              defaultValue=""
              value={state.solicitante || ""}
              onChange={(e) => {
                handleChange("solicitante", e.target.value);
              }}
              error={!!errors.solicitante || undefined}
            >
              {listaPersonal
                .filter((p) => p.Rol === "cliente" && p.Estado === "activo" && p.oculto === false)
                .map((personal) => (
                  <MenuItem
                    key={personal.idProveedor}
                    value={personal.idProveedor}
                  >
                    {/* {personal.Nombre + " " + personal.Apellido} */}
                    {personal.ApellidoyNombre}
                  </MenuItem>
                ))}
            </Select>
            {errors.solicitante && (
              <Typography color="error" variant="body2">
                {errors.solicitante}
              </Typography>
            )}
          </div>
        </div>
        <div className={classes.formRow}>
          <div className={classes.halfField}>
            <Typography variant="subtitle1">Código de presupuesto</Typography>
            <TextField
              variant="outlined"
              fullWidth
              value={state.codigoPresupuesto || ""}
              onChange={(e) => handleChange("codigoPresupuesto", e.target.value)}
            />
          </div>
          <div className={classes.halfField}>
            <Typography variant="subtitle1">Días hábiles</Typography>
            <TextField
              variant="outlined"
              type="text"
              inputMode="numeric"
              fullWidth
              value={state.diasHabiles ?? ""}
              onChange={(e) => {
                const val = e.target.value;
                if (val === "" || (!/^\d+$/.test(val) || Number(val) >= 1 || Number(val) <= 99)) {
                  handleChange("diasHabiles", val);
                }
              }}
              inputProps={{ pattern: "[0-9]*" }}
            />
          </div>
        </div>

        {/* Fila de Fecha de Entrega y Plazo de Entrega */}
        <div className={classes.formRow}>
          <div className={classes.halfField}>
            <Typography variant="subtitle1">Fecha de Entrega</Typography>
            <TextField
              variant="outlined"
              type="date"
              fullWidth
              value={state.fechaEntrega}
              onChange={(e) => handleChange("fechaEntrega", e.target.value)}
            />
            {errors.fechaEntrega && (
              <Typography color="error" variant="body2">
                {errors.fechaEntrega}
              </Typography>
            )}
          </div>
          <div className={classes.halfField}>
            <Typography variant="subtitle1">Plazo de Entrega</Typography>
            <TextField
              variant="outlined"
              type="date"
              fullWidth
              value={state.plazoEntrega}
              onChange={(e) => handleChange("plazoEntrega", e.target.value)}
            />
            {errors.plazoEntrega && (
              <Typography color="error" variant="body2">
                {errors.plazoEntrega}
              </Typography>
            )}
          </div>
        </div>

        {/* Fila de ARS / USD, Importe y Fecha de Entrega */}
        <div className={classes.formRow}>
          <div
            style={{
              width: "32%",
            }}
          >
            <FormControl component="fieldset" className={classes.radioGroup}>
              <FormLabel component="legend">Moneda</FormLabel>
              <RadioGroup
                row
                value={currency}
                onChange={handleCurrencyChange}
                error={!!errors.moneda || undefined}
              >
                <FormControlLabel value="ARS" control={<Radio />} label="ARS" />
                <FormControlLabel value="USD" control={<Radio />} label="USD" />
              </RadioGroup>
            </FormControl>
            {errors.moneda && (
              <Typography color="error" variant="body2">
                {errors.moneda}
              </Typography>
            )}
          </div>
          {/* {currency === "USD" && (
            <div style={{ marginBottom: 16, width: "32%" }}>
              <Typography variant="subtitle1">
                Tipo de Cambio (ARS a USD)
              </Typography>
              <TextField
                variant="outlined"
                type="number"
                fullWidth
                value={tipoDeCambio}
                onChange={(e) => {
                  const nuevoCambio = parseFloat(e.target.value) || 1;
                  setTipoDeCambio(nuevoCambio);

                  const subtotal = items.reduce(
                    (acc, item) => acc + parseFloat(item.precioTotal || 0),
                    0
                  );
                  const totalConvertido = subtotal * nuevoCambio;
                  setImporteTotal(subtotal * nuevoCambio);

                  setState((prev) => ({
                    ...prev,
                    importeTotal: totalConvertido.toFixed(2), // o redondeado como prefieras
                  }));
                }}
              />
            </div>
          )} */}

          <div className={classes.conceptoField}>
            <div
              style={{ display: "flex", alignItems: "center", marginBottom: 4 }}
            >
              <Typography variant="subtitle1">Importe total</Typography>
              {/* {currency === "USD" && (
                <Tooltip
                  title={
                    <span style={{ fontSize: "0.8rem" }}>
                      Suma del precio total de los items agregados por (*) el
                      valor del tipo de cambio
                    </span>
                  }
                  arrow
                >
                  <InfoIcon
                    fontSize="small"
                    style={{ color: "rgba(0, 0, 0, 0.54)" }}
                  />
                </Tooltip>
              )} */}
            </div>
            <TextField
              variant="outlined"
              fullWidth
              value={formatearImporte(importeTotal)}
              InputProps={{ readOnly: true }}
              disabled
            />
          </div>
        </div>

        {/* Área blanca con botones Adjuntar y Nuevo */}
        <div className={classes.areaBlanca}>
          <div style={{ display: "flex", justifyContent: "space-between" }}>
            <Typography variant="subtitle1" className={classes.itemText}>
              Items
            </Typography>
            <div>
              {/* <Buttons */}
              <input
                type="file"
                ref={fileInputRef}
                style={{ display: "none" }}
              />
              <BotonGenerico
                tipo={"nuevo"}
                color="primary"
                onClick={handleNuevoClick}
                text="Nuevo"
              />
            </div>
          </div>

          {/* Mostrar la tabla y los botones solo cuando se haga click en "Nuevo" */}
          {items.length > 0 && (
            <TableContainer
              component={Paper}
              className={classes.tableContainer}
            >
              <Table>
                <TableHead>
                  <TableRow>
                    {/* <TableCell>Sector</TableCell> */}
                    {/* <TableCell align="center">Código</TableCell> */}
                    <TableCell align="center">Nombre</TableCell>
                    <TableCell align="center">Cantidad</TableCell>
                    <TableCell align="center">Precio unitario</TableCell>
                    <TableCell align="center">Precio total</TableCell>
                    <TableCell align="center">Opciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {items.map((item, index) => (
                    <TableRow key={index}>
                      {/* <TableCell>{item.sector}</TableCell> */}
                      {/* <TableCell align="center">{item.codigoItem}</TableCell> */}
                      <TableCell align="center">
                        {item.itemName || "Sin nombre"}
                      </TableCell>
                      <TableCell align="center">{item.cantidad}</TableCell>
                      <TableCell align="center">
                        {formatearImporte(item.precioUnitario)}
                      </TableCell>
                      <TableCell align="center">
                        {formatearImporte(item.precioTotal)}
                      </TableCell>
                      <TableCell align="center">
                        <Tooltip
                          title={<span style={{ fontSize: "0.8rem" }}>Editar</span>}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEditarItem(item, index)}
                          >
                            <EditIcon color="primary" />
                          </IconButton>
                        </Tooltip>
                        <Tooltip
                          title={<span style={{ fontSize: "0.8rem" }}>Eliminar</span>}
                        >
                          <IconButton
                            size="small"
                            onClick={() => handleEliminarItem(index)}
                          >
                            <DeleteIcon color="secondary" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </div>
        {errors.items && (
          <Typography color="error" variant="body2">
            {errors.items}
          </Typography>
        )}

        <AgregarItemsModal
          open={openItemsModal}
          onClose={handleCloseItemsModal}
          listaPersonal={listaPersonal}
          onGuardarItem={handleGuardarItem}
          resetFormulario={resetFormulario}
          initialItem={itemModalData}
        />
      </DialogContent>
      <DialogFooter>
        <BotonGenerico
          tipo={"guardar"}
          color="primary"
          onClick={handleSave}
          text="Guardar"
        />
      </DialogFooter>
    </Dialog>
  );
};
export default CrearOrdenDeServiciosModal;
